import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { PageHero } from '../components/PageHero';
import { motion } from 'framer-motion';
import { Ship, Warehouse, Truck, Wrench, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  anchor: Ship,
  ship: Ship,
  warehouse: Warehouse,
  truck: Truck,
  wrench: Wrench
};

export const ServicesPage = () => {
  const { t, language } = useLanguage();
  const [services, setServices] = useState([]);
  const [stats, setStats] = useState({ foundation_year: 1977, storage_spots: 200, employees: 12, brand_partners: 8 });
  const [siteImages, setSiteImages] = useState({ page_heroes: {} });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, statsRes, imagesRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/site-stats`),
          axios.get(`${API}/site-images`)
        ]);
        setServices(servicesRes.data);
        setStats(statsRes.data);
        setSiteImages(imagesRes.data);
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };
    fetchData();
  }, []);

  const getTitle = (service) => {
    const key = `title_${language}`;
    return service[key] || service.title_it;
  };

  const getDescription = (service) => {
    const key = `description_${language}`;
    return service[key] || service.description_it;
  };

  const getFeatures = (service) => {
    const key = `features_${language}`;
    return service[key] || service.features_it || [];
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/media/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    if (url.startsWith('/api/uploads')) return `${process.env.REACT_APP_BACKEND_URL}${url.replace('/api/uploads/', '/api/media/')}`;
    if (url.startsWith('/uploads')) return `${process.env.REACT_APP_BACKEND_URL}/api/media/${url.replace('/uploads/', '')}`;
    return url;
  };

  return (
    <div data-testid="services-page" className="pt-20">
      {/* Hero */}
      <PageHero
        title={t('services.title')}
        subtitle={t('services.subtitle')}
        backgroundUrl={siteImages.page_heroes?.services}
        darkMode={true}
      />

      {/* Services Detail */}
      {services.map((service, index) => {
        const IconComponent = iconMap[service.icon] || Ship;
        const isEven = index % 2 === 0;
        const features = getFeatures(service);

        return (
          <section
            key={service.id}
            data-testid={`service-section-${service.key}`}
            className={`py-24 ${isEven ? 'bg-white' : 'bg-[#F5F5F7]'}`}
          >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                {/* Image */}
                <motion.div
                  initial={{ opacity: 0, x: isEven ? -30 : 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={`${!isEven ? 'lg:order-2' : ''}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={getImageUrl(service.image)}
                      alt={getTitle(service)}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>

                {/* Content */}
                <motion.div
                  initial={{ opacity: 0, x: isEven ? 30 : -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className={`${!isEven ? 'lg:order-1' : ''}`}
                >
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 bg-[#0B1221] rounded-full flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <h2 className="text-3xl md:text-4xl font-bold text-[#0B1221]">
                      {getTitle(service)}
                    </h2>
                  </div>

                  <p className="text-[#6E6E73] text-lg mb-8 leading-relaxed">
                    {getDescription(service)}
                  </p>

                  {features.length > 0 && (
                    <ul className="space-y-4 mb-8">
                      {features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-3">
                          <div className="w-5 h-5 bg-[#FF3B30] rounded-full flex items-center justify-center flex-shrink-0">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-[#0B1221]">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <Link to="/quote">
                    <Button
                      data-testid={`service-cta-${service.key}`}
                      className="bg-[#0B1221] text-white hover:bg-[#1A2639] rounded-full px-8 py-4 text-sm font-medium"
                    >
                      {t('nav.quote')}
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Stats Section */}
      <section className="py-24 bg-[#0B1221]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.foundation_year}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Anno di fondazione' : language === 'de' ? 'Gründungsjahr' : language === 'fr' ? 'Année de fondation' : 'Year of foundation'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.storage_spots}+</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Posti deposito' : language === 'de' ? 'Lagerplätze' : language === 'fr' ? 'Places de dépôt' : 'Storage spots'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.employees}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Specialisti' : language === 'de' ? 'Spezialisten' : language === 'fr' ? 'Spécialistes' : 'Specialists'}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="text-4xl md:text-5xl font-bold text-white mb-2">{stats.brand_partners}</div>
              <p className="text-[#86868B] text-sm uppercase tracking-wider">
                {language === 'it' ? 'Brand partner' : language === 'de' ? 'Markenpartner' : language === 'fr' ? 'Partenaires de marque' : 'Brand partners'}
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};
